import { WbsItemTypeVO } from '../../../../../../../../domain/value-object/WbsItemTypeVO'
import { ProjectPlanCumulation } from '../../../../../../../../lib/functions/projectPlan'
import projectDefaultIcon from '../../../../../../../../assets/project_icon_default.svg'
import { GanttBarVisualType, getGanttBarVisualType } from './ganttUtil'

/**
 * GanttRowItem.type が取り得る値。
 * type の文字列比較はこの定数と ganttRowItemService の判定関数に閉じ込める。
 */
export const GANTT_ROW_TYPE = {
  PROGRAM: 'PROGRAM',
  GROUP: 'GROUP',
  PROJECT: 'PROJECT',
  WORKGROUP: 'WORKGROUP',
  PROCESS: 'PROCESS',
  TASK: 'TASK',
  DELIVERABLE: 'DELIVERABLE',
} as const

/** ガントチャートが表示に使う行データの抽象型 */
export type GanttRowItem = {
  /** AG Grid の行 uuid（= projectPlanUuid）。行の同一性・行検索に使う */
  uuid: string
  /** 行に対応する WbsItem の UUID。依存線・クリティカルパスの保存に使う */
  wbsItemUuid?: string
  scheduledDate?: {
    startDate?: string
    endDate?: string
  }
  actualDate?: { startDate?: string; endDate?: string }
  projectPlanCumulation?: ProjectPlanCumulation
  status?: string
  displayName?: string
  type: string
  wbsItemType?: WbsItemTypeVO
  iconUrl?: string
  responsible?: { name?: string; iconUrl?: string }
  progressRate?: number
  scheduledProgressRate?: number
  /** 予定線（バー）をドラッグ編集できる行か */
  editable?: boolean
}

/**
 * GanttRowItem に対する判定・変換ロジックを集約したサービス。
 * WbsItemTypeVO がある場合はそのメソッドを利用し、なければ文字列 type でフォールバックする。
 */

// WbsItemTypeVO の有無に関わらず型判定できるよう、構造的部分型で受ける
type TypeCheckable =
  | {
      wbsItemType?: {
        isWorkgroup?: () => boolean
        isProcess?: () => boolean
        isTask?: () => boolean
        isDeliverable?: () => boolean
      }
      type?: string
    }
  | undefined

const isWorkgroup = (rowItem: TypeCheckable): boolean =>
  rowItem?.wbsItemType?.isWorkgroup?.() ??
  rowItem?.type === GANTT_ROW_TYPE.WORKGROUP

const isProcess = (rowItem: TypeCheckable): boolean =>
  rowItem?.wbsItemType?.isProcess?.() ??
  rowItem?.type === GANTT_ROW_TYPE.PROCESS

const isTask = (rowItem: TypeCheckable): boolean =>
  rowItem?.wbsItemType?.isTask?.() ?? rowItem?.type === GANTT_ROW_TYPE.TASK

const isDeliverable = (rowItem: TypeCheckable): boolean =>
  rowItem?.wbsItemType?.isDeliverable?.() ??
  rowItem?.type === GANTT_ROW_TYPE.DELIVERABLE

// PROGRAM/GROUP/PROJECT はプログラムの管理画面専用の行タイプ
// （wbsItemType を持たないため文字列 type のみで判定する）
const isProgram = (rowItem: TypeCheckable): boolean =>
  rowItem?.type === GANTT_ROW_TYPE.PROGRAM

const isGroup = (rowItem: TypeCheckable): boolean =>
  rowItem?.type === GANTT_ROW_TYPE.GROUP

const isProject = (rowItem: TypeCheckable): boolean =>
  rowItem?.type === GANTT_ROW_TYPE.PROJECT

const getIconUrl = (
  rowItem:
    | { type?: string; iconUrl?: string; wbsItemType?: { iconUrl?: string } }
    | undefined
): string | undefined => {
  const iconUrl = rowItem?.iconUrl ?? rowItem?.wbsItemType?.iconUrl
  if (iconUrl) return iconUrl
  // アイコン未設定のプロジェクト行はデフォルトアイコンを表示する
  // （ProgramProjectReport と同じ project_icon_default.svg）
  if (rowItem?.type === GANTT_ROW_TYPE.PROJECT) return projectDefaultIcon
  return undefined
}

const getBarVisualType = (
  rowItem: GanttRowItem,
  hasChildren: boolean
): GanttBarVisualType => {
  if (rowItem.wbsItemType) {
    return getGanttBarVisualType(rowItem.wbsItemType, hasChildren)
  }
  // PROJECT はサマリー行として WORKGROUP/PROGRAM と同じ予定線（workgroup バー）で表示する
  if (
    isProgram(rowItem) ||
    isGroup(rowItem) ||
    isProject(rowItem) ||
    isWorkgroup(rowItem)
  ) {
    return 'workgroup'
  }
  if (isProcess(rowItem)) return 'process'
  return 'task'
}

export const ganttRowItemService = {
  isWorkgroup,
  isProcess,
  isTask,
  isDeliverable,
  isProgram,
  isGroup,
  isProject,
  getIconUrl,
  getBarVisualType,
}
